import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal, Button, Form, Row, Col, Table, Spinner, Alert,
} from 'react-bootstrap';
import axios from '../axiosAlertConfig';

const toMoney = (v) =>
  Number(v || 0).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const fmtCount = (accepted, failed) => `${Number(accepted || 0)} / ${Number(failed || 0)}`;

export default function AlertMessageUsageAccountModal({
  show,
  onHide,
  iAccountID,
  highlightIncidentID = null,
  user,
}) {
  const [months, setMonths] = useState(3);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [loading, setLoading] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);
  const [error, setError] = useState('');

  const [report, setReport] = useState({
    summary: {},
    dispatches: [],
    incidents: [],
  });

  const [pricing, setPricing] = useState({
    smsUnitCost: '0.0000',
    voiceUnitCost: '0.0000',
    emailUnitCost: '0.0000',
    resolvedFrom: 'ACCOUNT',
  });

  const displayName = useMemo(() => {
    return [
      user?.firstName ?? user?.FirstName,
      user?.lastName ?? user?.LastName,
    ].filter(Boolean).join(' ') || user?.userName || user?.username || '';
  }, [user]);

  const load = useCallback(async () => {
    if (!show || !iAccountID) return;

    try {
      setLoading(true);
      setError('');

      const params = { iAccountID, months };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const [usageRes, pricingRes] = await Promise.all([
        axios.get('/ialert/message-usage', { params }),
        axios.get('/ialert/message-usage/pricing', { params: { iAccountID } }),
      ]);

      setReport(usageRes.data || { summary: {}, dispatches: [], incidents: [] });

      const p = pricingRes.data?.pricing || {};
      setPricing({
        smsUnitCost: String(p.smsUnitCost ?? '0.0000'),
        voiceUnitCost: String(p.voiceUnitCost ?? '0.0000'),
        emailUnitCost: String(p.emailUnitCost ?? '0.0000'),
        resolvedFrom: p.resolvedFrom || 'ACCOUNT',
      });
    } catch (e) {
      console.error('Load message usage failed', e);
      setError(e?.response?.data?.message || 'Failed to load message usage');
    } finally {
      setLoading(false);
    }
  }, [show, iAccountID, months, dateFrom, dateTo]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const savePricing = async () => {
    try {
      setSavingPricing(true);
      setError('');

      await axios.put('/ialert/message-usage/pricing', {
        iAccountID,
        smsUnitCost: pricing.smsUnitCost,
        voiceUnitCost: pricing.voiceUnitCost,
        emailUnitCost: pricing.emailUnitCost,
        updatedByUserID: user?.userID || null,
        updatedByName: displayName || null,
      });

      await load();
    } catch (e) {
      console.error('Save pricing failed', e);
      setError(e?.response?.data?.message || 'Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const s = report?.summary || {};
  const dispatches = Array.isArray(report?.dispatches) ? report.dispatches : [];
  const incidents = Array.isArray(report?.incidents) ? report.incidents : [];

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Message Usage — Account</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        {error ? <Alert variant="danger">{error}</Alert> : null}

        <Row className="g-2 mb-3">
          <Col md={2}>
            <Form.Label>Months</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={24}
              value={months}
              onChange={(e) => setMonths(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Form.Label>Date From</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Form.Label>Date To</Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Col>

          <Col md={2} className="d-flex align-items-end">
            <Button variant="outline-primary" onClick={load} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
            </Button>
          </Col>
        </Row>
{/* 
        <Row className="g-2 mb-2">
          <Col md={4}>
            <Form.Label>Cost per SMS</Form.Label>
            <Form.Control
              type="number"
              step="0.0001"
              value={pricing.smsUnitCost}
              onChange={(e) => setPricing((p) => ({ ...p, smsUnitCost: e.target.value }))}
            />
          </Col>

          <Col md={4}>
            <Form.Label>Cost per Voice</Form.Label>
            <Form.Control
              type="number"
              step="0.0001"
              value={pricing.voiceUnitCost}
              onChange={(e) => setPricing((p) => ({ ...p, voiceUnitCost: e.target.value }))}
            />
          </Col>

          <Col md={4}>
            <Form.Label>Cost per Email</Form.Label>
            <Form.Control
              type="number"
              step="0.0001"
              value={pricing.emailUnitCost}
              onChange={(e) => setPricing((p) => ({ ...p, emailUnitCost: e.target.value }))}
            />
          </Col>
        </Row>

        <div className="d-flex align-items-center gap-2 mb-3">
          <Button variant="primary" onClick={savePricing} disabled={savingPricing}>
            {savingPricing ? 'Saving…' : 'Save Pricing'}
          </Button>
          <small className="text-muted">
            Historical rows keep the cost stored when that dispatch batch was recorded.
            Current pricing is used for future dispatches.
          </small>
        </div> */}

        <h6>Cumulative Total</h6>
        <Table bordered striped size="sm" className="mb-4">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Requested</th>
              <th>Accepted</th>
              <th>Failed</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>SMS</td>
              <td>{Number(s.smsRequested || 0)}</td>
              <td>{Number(s.smsAccepted || 0)}</td>
              <td>{Number(s.smsFailed || 0)}</td>
              <td>{toMoney(s.smsCost || 0)}</td>
            </tr>
            <tr>
              <td>Voice</td>
              <td>{Number(s.voiceRequested || 0)}</td>
              <td>{Number(s.voiceAccepted || 0)}</td>
              <td>{Number(s.voiceFailed || 0)}</td>
              <td>{toMoney(s.voiceCost || 0)}</td>
            </tr>
            <tr>
              <td>Email</td>
              <td>{Number(s.emailRequested || 0)}</td>
              <td>{Number(s.emailAccepted || 0)}</td>
              <td>{Number(s.emailFailed || 0)}</td>
              <td>{toMoney(s.emailCost || 0)}</td>
            </tr>
            <tr>
              <td><strong>Total</strong></td>
              <td><strong>{Number(s.totalMessagesRequested || 0)}</strong></td>
              <td><strong>{Number(s.totalMessagesAccepted || 0)}</strong></td>
              <td><strong>{Number(s.totalMessagesFailed || 0)}</strong></td>
              <td><strong>{toMoney(s.totalCost || 0)}</strong></td>
            </tr>
          </tbody>
        </Table>

        <h6>Each Alert Initiation / Dispatch Batch</h6>
        <Table bordered hover size="sm" className="mb-4">
          <thead>
            <tr>
              <th>Dispatch Time</th>
              <th>Incident</th>
              <th>Type / Category</th>
              <th>SMS A/F</th>
              <th>Voice A/F</th>
              <th>Email A/F</th>
              <th>Webpush</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {dispatches.length ? dispatches.map((row) => (
              <tr
                key={row.usageBatchID}
                className={String(row.incidentID) === String(highlightIncidentID) ? 'table-warning' : ''}
              >
                <td>{fmtDate(row.dispatchCreatedAt)}</td>
                <td>{row.incidentID}</td>
                <td>
                  <div>{row.incidentType || '—'}</div>
                  <small className="text-muted">
                    {(row.incidentCategory || '—')} {row.buildingName ? `• ${row.buildingName}` : ''}
                  </small>
                </td>
                <td>{fmtCount(row.smsAccepted, row.smsFailed)}</td>
                <td>{fmtCount(row.voiceAccepted, row.voiceFailed)}</td>
                <td>{fmtCount(row.emailAccepted, row.emailFailed)}</td>
                <td>{Number(row.webpushQueued || 0)}</td>
                <td>{toMoney(row.totalCost || 0)}</td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="text-center text-muted">No dispatches in range.</td></tr>
            )}
          </tbody>
        </Table>

        <h6>Per Incident Total</h6>
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th>Incident</th>
              <th>Latest Dispatch</th>
              <th>Dispatches</th>
              <th>SMS A/F</th>
              <th>Voice A/F</th>
              <th>Email A/F</th>
              <th>Webpush</th>
              <th>Total Cost</th>
            </tr>
          </thead>
          <tbody>
            {incidents.length ? incidents.map((row) => (
              <tr
                key={`${row.iAccountID}-${row.incidentID}`}
                className={String(row.incidentID) === String(highlightIncidentID) ? 'table-warning' : ''}
              >
                <td>
                  <div>{row.incidentID}</div>
                  <small className="text-muted">
                    {(row.incidentType || '—')} / {(row.incidentCategory || '—')}
                    {row.buildingName ? ` • ${row.buildingName}` : ''}
                  </small>
                </td>
                <td>{fmtDate(row.latestDispatchAt)}</td>
                <td>{Number(row.dispatchCount || 0)}</td>
                <td>{fmtCount(row.smsAccepted, row.smsFailed)}</td>
                <td>{fmtCount(row.voiceAccepted, row.voiceFailed)}</td>
                <td>{fmtCount(row.emailAccepted, row.emailFailed)}</td>
                <td>{Number(row.webpushQueued || 0)}</td>
                <td>{toMoney(row.totalCost || 0)}</td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="text-center text-muted">No incidents in range.</td></tr>
            )}
          </tbody>
        </Table>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}